/**
 * Confirmation d'abandon d'une run (mode illimité, §7.6).
 *
 * Un abandon est **irréversible** : la run se termine et le solde du round en cours n'est pas
 * versé. L'écran demande donc une confirmation explicite (`role="alertdialog"`) au lieu d'un
 * simple bouton : sur téléphone, une cible tactile peut être touchée par erreur.
 *
 * Le composant ne décide de rien et n'appelle pas l'API : il remonte la décision à l'écran de jeu,
 * qui seul connaît l'identifiant de la run et envoie `POST /runs/:id/end {reason:'abandon'}`.
 */
import { useTranslation } from 'react-i18next';

import { Bouton } from './Bouton';

export interface ProprietesConfirmationAbandon {
  /** Une requête d'abandon est en cours (verrou d'interface). */
  readonly enCours: boolean;
  readonly onConfirmer: () => void;
  readonly onAnnuler: () => void;
}

export function ConfirmationAbandon({
  enCours,
  onConfirmer,
  onAnnuler,
}: ProprietesConfirmationAbandon) {
  const { t } = useTranslation();

  return (
    <section
      className="panneau panneau--abandon"
      role="alertdialog"
      aria-modal="true"
      aria-labelledby="titre-abandon"
      aria-describedby="texte-abandon"
      data-confirmation-abandon="oui"
    >
      <h2 className="panneau__titre" id="titre-abandon">
        {t('jeu.illimite.abandon.titre')}
      </h2>
      <p className="note note--alerte" id="texte-abandon">
        {t('jeu.illimite.abandon.texte')}
      </p>
      <div className="panneau__actions">
        <Bouton variante="secondaire" onClick={onAnnuler} disabled={enCours}>
          {t('jeu.illimite.abandon.annuler')}
        </Bouton>
        <Bouton variante="principal" onClick={onConfirmer} enCours={enCours}>
          {t('jeu.illimite.abandon.confirmer')}
        </Bouton>
      </div>
    </section>
  );
}
